// Import and re-export types from lesson module
import type { Lesson, LessonStatus } from './lesson';
export type { Lesson, LessonStatus };

/**
 * Minutes after sending within which a player or coach may still edit or
 * delete a message. Applies to both: a message that has been sitting in
 * someone's thread for an hour has been read, and silently rewriting or
 * removing it then is worse than leaving it.
 */
export const MESSAGE_EDIT_WINDOW_MINUTES = 60;

/** One entry per save in a message's edit history. */
export interface MessageEditRecord {
  previousText: string;
  editedAt: string;      // ISO string (client clock)
  editedBy: string;      // userId
  editedByName: string;
}

/**
 * The six Tapbacks, in Apple's order. Stored as these stable keys rather than
 * as the emoji itself so the glyph can change without rewriting message
 * documents — and so the map stays legible in the Firestore console.
 */
export const REACTION_KEYS = ['heart', 'thumbsUp', 'thumbsDown', 'haha', 'exclaim', 'question'] as const;

export type ReactionKey = typeof REACTION_KEYS[number];

export const REACTION_EMOJI: Record<ReactionKey, string> = {
  heart: '\u2764\uFE0F',
  thumbsUp: '\uD83D\uDC4D',
  thumbsDown: '\uD83D\uDC4E',
  haha: '\uD83D\uDE02',
  exclaim: '\u203C\uFE0F',
  question: '\u2753',
};

/** Accessible name for each Tapback, used on the picker buttons. */
export const REACTION_LABEL: Record<ReactionKey, string> = {
  heart: 'Heart',
  thumbsUp: 'Thumbs up',
  thumbsDown: 'Thumbs down',
  haha: 'Haha',
  exclaim: 'Exclamation',
  question: 'Question mark',
};

export type MessageType =
  | 'text'
  | 'video'
  | 'lesson_request'
  | 'lesson_delivered'
  | 'lesson_review_submitted'
  // Written by CoachBundleProgress.onFinalSubmit immediately after the
  // final-lesson submit + saveProgressReport pair. Renders as the warm-
  // amber "Progress Report" card directly below the lesson_delivered hero
  // card. The associated progressReports doc ID is on `progressReportId`.
  | 'progress_report_delivered'
  | 'system'
  // Sent by a coach to share a drill video directly in a conversation.
  // Uses the original (4K) drill.videoUrl — not the web-optimised version.
  | 'drill_attachment'
  | 'pro_video_attachment'
  | 'image'
  // Live lessons v2.0: a Suggest a Time or Request a Time card (timeCard).
  | 'live_time_card'
  // Rule 9: a call in the pair's room outside every lesson (roomCall).
  | 'live_room_call'
  | 'live_weekly_proposal'
  | 'live_lesson_time_proposal';

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  senderAvatarUrl?: string;
  type: MessageType;
  text?: string;
  videoUrl?: string;  // For video messages
  videoStoragePath?: string;  // For video messages
  thumbnailUrl?: string;  // For video messages
  imageUrl?: string;  // For image messages

  // Reply metadata
  replyToMessageId?: string;  // ID of message being replied to
  replyToText?: string;  // Cached text/caption of original message
  replyToSenderName?: string;  // Cached sender name of original message
  replyToType?: MessageType;  // Type of original message (text/video)

  // Live lessons v2.0 (docs/live-lessons-data.md, conversations/messages)
  timeCard?: { cardId: string; kind: 'suggest' | 'request'; sentBy: 'coach' | 'player'; startAt: string; durationMinutes: number };
  /** Rule 9: a call in the pair's room outside every lesson (live_room_call). */
  roomCall?: { roomId: string; requestedBy: 'coach' | 'player' };
  liveEvent?: string;
  /** Decision 126 (docs/booking-contracts.md 11): a lesson event line's lesson and its card title. */
  bookingId?: string;
  lessonTitle?: string;
  /** A support message's action (docs/booking-contracts.md): Suggest New Times, or Pick a New Time (decision 90). */
  action?: { kind: 'suggest_new_times' | 'pick_new_time' | 'refund_instead'; label: string; link?: string; bookingId?: string };
  /** Every action, the first being `action` (decision 84: Suggest New Times and Refund instead). */
  actions?: Array<{ kind: 'suggest_new_times' | 'pick_new_time' | 'refund_instead'; label: string; link?: string; bookingId?: string }>;
  /** Set once the message's actions are done with: rebooked, refunded or withdrawn. */
  resolved?: string;
  /** The coach's new times for one lesson (live_lesson_time_proposal, docs/booking-contracts.md 6). */
  lessonTimeProposal?: {
    proposalId: string; bookingId: string; lesson: string; fromStartAt: string;
    /** expiresAt: the start less the lead time; the chip is expired from then (decision 98). */
    times: Array<{ startAt: string; label: string; coachLabel: string; expiresAt?: string }>;
    keepLabel: string; pickLabel: string;
    status: 'open' | 'accepted' | 'kept' | 'picking' | 'replaced' | 'moved'; chosen?: number;
  };
  /** Decision 96: the coach's weekly time suggestion card (live_weekly_proposal). */
  weeklyProposal?: {
    proposalId: string; membershipId: string;
    slots: Array<{ weekday: number; time: string; coachWeekday: number; coachTime: string; startAt: string; label: string; coachLabel: string }>;
    keepLabel: string; pickLabel: string; hasWeeklyTime: boolean;
    status: 'open' | 'accepted' | 'kept' | 'picking' | 'replaced' | 'withdrawn'; chosen?: number;
  };


  // Lesson action metadata
  lessonId?: string;  // ID of the lesson this message relates to
  /** progressReports doc ID — set only on `progress_report_delivered` messages.
   *  Read by ProgressReportViewerModal to hydrate the report on click. */
  progressReportId?: string;

  // Drill attachment — set only on 'drill_attachment' messages
  drillId?: string;
  drillTitle?: string;
  drillDescription?: string;
  drillShotType?: string;
  drillVideoUrl?: string;      // Original 4K videoUrl from the drills collection
  drillThumbnailUrl?: string;
  drillDurationSeconds?: number;

  // Pro video attachment — set only on 'pro_video_attachment' messages
  proVideoId?: string;
  proVideoTitle?: string;
  proVideoPlayerName?: string;
  proVideoTags?: string[];
  proVideoUrl?: string;        // Snapshot of the URL at send time — immutable
  proVideoThumbnailUrl?: string;
  proVideoDurationSeconds?: number;

  // Double Bagel notification messages
  category?: string;  // e.g. 'WELCOME', 'TRAINING PROGRAM CONFIRMED'

  // Set when a staff member sends a message as Double Bagel via the support inbox.
  // Invisible to players/coaches; shown in the admin support view next to the timestamp.
  sentByStaffId?: string;
  sentByStaffName?: string;

  createdAt: string;
  readBy: string[];

  /**
   * Tapbacks, keyed by the reacting user's uid. One reaction per person, so
   * toggling is a single field write (`reactions.<uid>`) rather than a
   * read-modify-write of an array, and two people reacting at once cannot
   * clobber each other.
   */
  reactions?: Record<string, ReactionKey>;

  // Edit / delete lifecycle
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
  editedAt?: string;
  editHistory?: MessageEditRecord[];
}

export interface Conversation {
  id: string;
  participants: string[];  // Array of user IDs (minimum 2, but can support groups)
  participantDetails: {
    [userId: string]: {
      name: string;
      avatarUrl?: string;
    };
  };
  lastMessage?: string;
  lastMessageAt?: string;
  unreadCount: {
    [userId: string]: number;
  };
  /** Map of userId → timestamp when they started typing. Null or missing = not typing. */
  typing?: {
    [userId: string]: unknown | null;
  };
  createdAt: string;
  updatedAt: string;

  // Lessons within this conversation (one conversation can have multiple lessons)
  lessons?: Lesson[];  // Array of lessons between these participants

  coachId?: string;
  playerId?: string;
  offeringId?: string;
  offeringTitle?: string;
}

export interface Review {
  id: string;
  conversationId: string;
  coachId: string;
  playerId: string;
  playerName: string;
  playerAvatarUrl?: string;
  offeringId?: string;
  offeringTitle?: string;
  rating: number;  // 1-5 stars
  comment?: string;
  createdAt: string;
}
